import { useQueries, UseQueryResult } from '@tanstack/react-query';

import { fetchAllProviders } from '@api/services/provider';
import useQueryCacheKey from '@hooks/useQueryCacheKey';
import { Countries } from '@models/CountriesAndLanguages';
import { Provider } from '@models/Provider';

const useProvidersQueries = () => {
  const queryCacheKey = useQueryCacheKey();

  return useQueries({
    queries: Countries.map(country => ({
      queryFn: () => fetchAllProviders(country),
      queryKey: ['providers', country, ...queryCacheKey],
    })),
    combine: results => {
      const isPending = results.some(({ isPending }) => isPending);
      const isError = results.some(({ isError }) => isError);

      const data: Provider[] = isError
        ? []
        : results.reduce(
            (acc: Provider[], { data: { data } = {} }) => (data ? [...acc, ...data] : acc),
            []
          );

      return {
        ...results.at(0),
        data: data.length > 0 && !isPending ? data : undefined,
        error: results.find(({ error }) => !!error)?.error ?? null,
        isError,
        isFetching: results.some(({ isFetching }) => isFetching),
        isLoading: results.some(({ isLoading }) => isLoading),
        isPending,
        refetch: () => Promise.all(results.map(({ refetch }) => refetch())),
      } as unknown as UseQueryResult<Provider[] | undefined>;
    },
  });
};

export default useProvidersQueries;
